import type { EntryStatus } from '@/components/entry-card';

/**
 * A 4-phase machine local to an open entry card:
 * - `idle`      — show the "Cash out • {offer}" CTA.
 * - `confirm`   — Cancel / Confirm; the payout column shows the offer.
 * - `loading`   — the cash-out request is in flight.
 * - `confirmed` — terminal; cashed out, the card settles in place.
 *
 * Backend-agnostic: parameterized over the product's settled-entry type `TEntry`, stored opaquely and
 * handed back on success. All product specifics are injected by the `useEntryCashOut` adapter.
 */
export type CashOutPhase = 'idle' | 'confirm' | 'loading' | 'confirmed';

export type CashOutState<TEntry> = {
    phase: CashOutPhase;
    /** Pinned offer while on confirm/loading, so a background refresh can't move the price under the user. */
    offerOverride: number | null;
    /** The settled entry from a successful cash-out — source of the confirmed amount + date. */
    cashedOut: TEntry | null;
};

export type CashOutAction<TEntry> =
    | { type: 'PRESS_CASH_OUT'; offer: number }
    | { type: 'CANCEL' }
    | { type: 'SUBMIT'; offer: number }
    | { type: 'SUCCESS'; entry: TEntry }
    | { type: 'OFFER_CHANGE' }
    | { type: 'CONFIRM_OFFER_CHANGED' }
    | { type: 'ERROR' };

/** Seeds `confirmed` when the entry was already cashed out this session (survives list virtualization). */
export const initialCashOutState = <TEntry>(cashedOut: TEntry | null = null): CashOutState<TEntry> => ({
    phase: cashedOut ? 'confirmed' : 'idle',
    offerOverride: null,
    cashedOut,
});

export const cashOutReducer = <TEntry>(
    state: CashOutState<TEntry>,
    action: CashOutAction<TEntry>
): CashOutState<TEntry> => {
    switch (action.type) {
        // Pin the offer being confirmed so a background refresh can't move the price under the user.
        case 'PRESS_CASH_OUT':
            return state.phase === 'idle' ? { ...state, phase: 'confirm', offerOverride: action.offer } : state;
        case 'CANCEL':
            return state.phase === 'confirm' ? { ...state, phase: 'idle', offerOverride: null } : state;
        // Also from idle: Retry / Complete on the failure and price-change sheets.
        case 'SUBMIT':
            return state.phase === 'confirm' || state.phase === 'idle'
                ? { ...state, phase: 'loading', offerOverride: action.offer }
                : state;
        case 'SUCCESS':
            return { phase: 'confirmed', offerOverride: null, cashedOut: action.entry };
        // Backend re-priced mid-flight: back to idle on the live offer; the adapter shows the price-change sheet.
        case 'OFFER_CHANGE':
            return state.phase === 'loading' ? { ...state, phase: 'idle', offerOverride: null } : state;
        // Offer moved while on the confirm screen: back to idle (clearing the pin) so they re-confirm.
        case 'CONFIRM_OFFER_CHANGED':
            return state.phase === 'confirm' ? { ...state, phase: 'idle', offerOverride: null } : state;
        case 'ERROR':
            return state.phase === 'loading' ? { ...state, phase: 'idle', offerOverride: null } : state;
        default:
            return state;
    }
};

/** Confirmed entries show the blue "Cashed out" badge; every other phase shows none. */
export const selectCashOutStatus = (phase: CashOutPhase): EntryStatus | undefined =>
    phase === 'confirmed' ? 'cashedOut' : undefined;

export type CashOutPayoutView = {
    labelKey: 'payout' | 'cash_out';
    amount: number;
    /** `true` → bright white (settled), `false` → dimmed `neutral300`. */
    bright: boolean;
};

/**
 * The payout column per non-idle phase (idle keeps the card's own base payout, so it isn't handled here):
 * confirm / loading → "Cash out" / offer, dimmed; confirmed → "Payout" / cashed-out amount, bright.
 */
export const selectCashOutPayoutView = (
    phase: Exclude<CashOutPhase, 'idle'>,
    amounts: { offer: number; cashedOutAmount: number }
): CashOutPayoutView => {
    if (phase === 'confirmed') {
        return { labelKey: 'payout', amount: amounts.cashedOutAmount, bright: true };
    }
    return { labelKey: 'cash_out', amount: amounts.offer, bright: false };
};
